/**
 * The authenticated shell. Warm cream chrome (§11), a persistent rail on tablet/desktop that collapses to
 * icons (labels stay reachable by tooltip), and a labelled 44 px trigger opening an overlay on phones.
 * Navigation is GENERATED from the route table and filtered by capability — never hand-listed.
 * Inside an event, the rail becomes that event's workspace, so the operator never loses which event
 * they are in.
 */
import { BrandMark } from '@/components/shell/BrandMark';
import { rememberEvent } from '@/lib/recentEvents';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useMatch } from 'react-router-dom';
import { ChevronLeft, LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Sun, SunMoon, UserRound, WifiOff, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ROUTES, href } from '@/routes/table';
import { useSession } from '@/lib/session';
import { useConnectivity } from '@/lib/connectivity';
import { applyTheme, getThemePref, type ThemePref } from '@/lib/theme';
import { cn } from '@/lib/cn';
import { NavIcon } from './Icon';
import { NotificationBell } from './NotificationBell';
import { IdleGuard } from './IdleGuard';
import { FeedbackButton } from './Feedback';
import { ShortcutsButton } from './Shortcuts';
import { CommandPalette, PaletteButton } from './CommandPalette';
import { SchemaBanner } from './SchemaBanner';
import { PlatformBanner } from '@/components/PlatformBanner';
import { VersionLine } from './VersionLine';
import { UpdateAvailable } from './UpdateAvailable';
import { WhatsNewAnnouncer } from '@/features/whatsnew/WhatsNew';
import { CallDock, CallsProvider } from '@/features/calls/CallsProvider';
import { savePreference } from '@/lib/storageChoice';
import { supabase } from '@/lib/supabase';
import { ROLE_LABEL } from '@/lib/capabilities';
import { dateRange } from '@/lib/format';

function useEventHeader(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-header', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await supabase.from('events').select('id, event_name, start_date, end_date, pack_enabled, status, hidden_surfaces').eq('id', eventId!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function ConnectivityBanner() {
  const { online, reachable, retry } = useConnectivity();
  const [checking, setChecking] = useState(false);
  if (online && !reachable) {
    return (
      <div role="alert" className="z-banner flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-st-warning px-4 py-2 text-center text-sm font-semibold text-on-danger" data-testid="unreachable-banner">
        <WifiOff className="size-4" aria-hidden />
        The server isn’t answering — changes can’t be saved right now. Nothing you’ve saved is lost.
        <button className="underline underline-offset-4" disabled={checking} onClick={async () => { setChecking(true); await retry(); setChecking(false); }}>{checking ? 'Checking…' : 'Try again'}</button>
      </div>
    );
  }
  if (online) return null;
  return (
    <div role="alert" className="z-banner flex items-center justify-center gap-2 bg-st-danger px-4 py-2 text-center text-sm font-semibold text-on-danger" data-testid="offline-banner">
      <WifiOff className="size-4" aria-hidden />
      You're offline — changes can't be saved until the connection returns. Nothing you've saved is lost.
    </div>
  );
}

function ThemeSwitch() {
  const [pref, setPref] = useState<ThemePref>(getThemePref());
  const next: Record<ThemePref, ThemePref> = { system: 'light', light: 'dark', dark: 'system' };
  const label = { system: 'Theme: follow system', light: 'Theme: light', dark: 'Theme: dark' }[pref];
  const I = pref === 'dark' ? Moon : pref === 'light' ? Sun : SunMoon;
  return (
    <button
      className="inline-flex size-9 items-center justify-center rounded-control text-ink-2 hover:bg-surface-2 hover:text-ink"
      aria-label={label}
      title={label}
      onClick={() => {
        const p = next[pref];
        applyTheme(p);
        setPref(p);
      }}
    >
      <I className="size-4" aria-hidden />
    </button>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { session, can, signOut } = useSession();
  const location = useLocation();
  const eventMatch = useMatch('/events/:eventId/*');
  const eventId = eventMatch?.params.eventId;
  const { data: event } = useEventHeader(eventId);
  useEffect(() => { if (event?.id) rememberEvent(String(event.id)); }, [event?.id]);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('rmp2.nav.collapsed') === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  // Focus the page heading on route change so screen-reader and keyboard users land on the new content.
  useEffect(() => {
    setMobileOpen(false);
    let tries = 0;
    const focusHeading = () => {
      // a deep link to a section (#anchor) keeps its own focus target (A11)
      if (location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1)))?.contains(document.activeElement)) return;
      const h = document.querySelector<HTMLElement>('[data-route-heading]');
      if (h) { h.focus({ preventScroll: true }); return; }
      // the page's code may still be loading (it waits inside the shell): the content area now, its heading when it
      // arrives — unless the person has already moved focus themselves
      if (tries === 0) mainRef.current?.focus({ preventScroll: true });
      const idle = document.activeElement === mainRef.current || document.activeElement === document.body;
      if (++tries < 50 && idle) t = window.setTimeout(focusHeading, 100);
    };
    let t = window.setTimeout(focusHeading, 60);
    return () => window.clearTimeout(t);
    // only a new page moves focus; the hash is read, not watched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const items = useMemo(() => {
    const area = eventId ? 'event' : 'org';
    const hidden = new Set<string>(eventId ? ((event?.hidden_surfaces as string[] | undefined) ?? []) : []);
    return ROUTES.filter((r) => r.area === area && r.nav && (!r.capability || can(r.capability)) && !(r.hideKey && hidden.has(r.hideKey)))
      .sort((a, b) => a.nav!.order - b.nav!.order)
      .map((r) => ({ ...r, to: eventId ? href(r.id, { eventId }) : r.path }));
  }, [eventId, can, event]);

  if (session.state !== 'authenticated-single-tenant') return <>{children}</>;

  const nav = (compact: boolean) => (
    <nav aria-label={eventId ? 'Event' : 'Workspace'} className="-m-1 flex min-h-0 flex-col gap-0.5 overflow-y-auto p-1">
      {eventId && (
        <Link to="/events" className={cn('mb-2 flex items-center gap-2 rounded-control px-2.5 py-2 text-[13px] text-ink-2 hover:bg-shell-hairline/50 hover:text-ink', compact && 'justify-center')} title="All events">
          <ChevronLeft className="size-4" aria-hidden />
          {!compact && 'All events'}
        </Link>
      )}
      {eventId && !compact && event && (
        <div className="mb-3 px-2.5">
          <div className="truncate text-sm font-semibold text-ink" title={event.event_name}>{event.event_name}</div>
          <div className="text-[12px] text-muted">{dateRange(event.start_date, event.end_date)}</div>
        </div>
      )}
      {items.map((r) => (
        <NavLink
          key={r.id}
          to={r.to}
          end={r.id === 'event-overview'}
          title={compact ? r.nav!.label : undefined}
          aria-label={compact ? r.nav!.label : undefined}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 rounded-control px-2.5 py-2 text-sm text-ink-2 transition-colors dur-fast hover:bg-shell-hairline/50 hover:text-ink',
              compact && 'justify-center',
              isActive && 'bg-surface font-semibold text-ink ring-1 ring-shell-hairline',
            )
          }
        >
          <NavIcon name={r.nav!.icon} />
          {!compact && r.nav!.label}
        </NavLink>
      ))}
    </nav>
  );

  const brand = (compact: boolean) => (
    <Link to="/events" className="flex items-center gap-2.5 px-1" aria-label="Booklyn Room Map Pro — all events">
      <BrandMark className="h-6" />
      {!compact && <span className="text-[15px] font-semibold tracking-tight text-ink">Room Map Pro</span>}
    </Link>
  );

  const userBlock = (compact: boolean) => (
    <div className={cn('mt-auto flex flex-col gap-1 border-t border-shell-hairline pt-3', compact && 'items-center')}>
      <Link to="/account" className={cn('flex items-center gap-2.5 rounded-control px-2.5 py-2 text-sm hover:bg-shell-hairline/50', compact && 'justify-center')} title={compact ? session.displayName : undefined} aria-label={compact ? 'Your account' : undefined}>
        <UserRound className="size-4 text-ink-2" aria-hidden />
        {!compact && (
          <span className="min-w-0">
            <span className="block truncate font-medium text-ink">{session.displayName}</span>
            <span className="block truncate text-[12px] text-muted">{ROLE_LABEL[session.role]} · {session.orgName}</span>
          </span>
        )}
      </Link>
      <button onClick={() => void signOut()} className={cn('flex items-center gap-2.5 rounded-control px-2.5 py-2 text-sm text-ink-2 hover:bg-shell-hairline/50 hover:text-ink', compact && 'justify-center')} aria-label={compact ? 'Sign out' : undefined} title={compact ? 'Sign out' : undefined}>
        <LogOut className="size-4" aria-hidden />
        {!compact && 'Sign out'}
      </button>
      <UpdateAvailable variant="rail" compact={compact} />
      <VersionLine compact={compact} />
    </div>
  );

  return (
    <div data-app-shell className="relative flex h-dvh flex-col overflow-hidden bg-canvas">
      <ConnectivityBanner />
      <PlatformBanner />
      {can('settings.manage') && <SchemaBanner />}
      {session.sessionTimeoutMinutes && <IdleGuard minutes={session.sessionTimeoutMinutes} />}
      <WhatsNewAnnouncer userId={session.userId} />
      <CallsProvider userId={session.userId}>
      <div className="flex min-h-0 flex-1">
        <aside className={cn('hidden shrink-0 flex-col gap-4 border-r border-shell-hairline bg-shell p-3 transition-[width] dur-base md:flex', collapsed ? 'w-16' : 'w-60')} aria-label="Primary">
          <div className={cn('flex items-center justify-between', collapsed && 'flex-col gap-2')}>
            {brand(collapsed)}
            <button
              className="inline-flex size-8 items-center justify-center rounded-control text-ink-2 hover:bg-shell-hairline/50"
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              onClick={() => {
                setCollapsed((c) => {
                  try {
                    savePreference('rmp2.nav.collapsed', c ? '0' : '1');
                  } catch {
                    /* not persisted */
                  }
                  return !c;
                });
              }}
            >
              {collapsed ? <PanelLeftOpen className="size-4" aria-hidden /> : <PanelLeftClose className="size-4" aria-hidden />}
            </button>
          </div>
          {nav(collapsed)}
          {userBlock(collapsed)}
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-overlay md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
            <button className="absolute inset-0 bg-scrim" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col gap-4 bg-shell p-4 animate-rise">
              <div className="flex items-center justify-between">
                {brand(false)}
                <button className="inline-flex size-11 items-center justify-center rounded-control" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
                  <X className="size-5" aria-hidden />
                </button>
              </div>
              {nav(false)}
              {userBlock(false)}
            </div>
          </div>
        )}

        {/* a call on in this browser: its panel, between the navigation and the page */}
        {/* the call panel: over the page from 1024 px (anchored here, beside the nav), a column beside it from 1366 px */}
        <div className="relative flex shrink-0 lg:max-[1366px]:w-0"><CallDock /></div>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-2 border-b border-shell-hairline bg-shell px-3 md:bg-canvas md:px-6">
            <button className="inline-flex size-11 items-center justify-center rounded-control md:hidden" aria-label="Open navigation" onClick={() => setMobileOpen(true)}>
              <Menu className="size-5" aria-hidden />
            </button>
            <div className="md:hidden">{brand(true)}</div>
            <div className="min-w-0 flex-1 truncate text-sm text-muted">
              {session.sandboxOrg && <span className="mr-2 rounded-chip bg-brand-tint px-1.5 py-0.5 text-[12px] font-semibold text-ink ring-1 ring-brand/40" title="Everyone who tries the demo shares this workspace, and it can be reset. For a real event, create an account for your organization.">Demo workspace<span className="hidden lg:inline"> · shared, can be reset</span></span>}
              <span className="hidden sm:inline">{session.orgName}</span>
            </div>
            <PaletteButton />
            <ShortcutsButton />
            <CommandPalette />
            <FeedbackButton />
            {/* display: contents — no box (and no flex gap) until the icon shows */}
            <div className="contents md:hidden"><UpdateAvailable variant="header" /></div>
            <NotificationBell />
            <ThemeSwitch />
          </header>
          <main id="main" ref={mainRef} tabIndex={-1} className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-[var(--call-bar-h,0px)] outline-none">
            <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
          </main>
        </div>
      </div>
      </CallsProvider>
    </div>
  );
}
